/**
 * useAdminThreads — Fetch and manage threads for admin/staff views.
 * Extends the existing useThreads hook with admin-specific capabilities.
 */

import { supabase } from "@/integrations/supabase/client";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "./useAuth";
import { THREAD_TRIAGE_EMBED, type TriageRow } from "@/lib/contactTriage";

export type ThreadStatus = "open" | "waiting_reply" | "closed";
export type ThreadCategory = "general" | "document" | "payment" | "status" | "other";

export interface AdminThread {
  id:             string;
  subject:        string;
  status:         ThreadStatus;
  category:       ThreadCategory;
  channel:        string | null;
  created_at:     string;
  updated_at:     string;
  application_id: string | null;
  user_id:        string | null;
  lead_id:        string | null;
  /** Staff owner of this conversation; null = follows the application / contact / lead assignee. */
  assigned_to?:   string | null;
  service_requests?: { id: string }[];
  // joined
  profiles?: {
    id: string;
    email: string | null;
    given_name: string | null;
    surname: string | null;
    profile_photo_url: string | null;
    tags?: string[] | null;
    user_type?: string | null;
    /** Portal language (ISO 639-1) — default reply language. */
    preferred_language?: string | null;
  } | null;
  applications?: {
    id: string;
    membership_package: string | null;
    status: string | null;
  } | null;
  /** Populated for lead-only threads (e.g. AI call center callers). */
  leads?: {
    id: string;
    first_name: string | null;
    last_name: string | null;
    email: string | null;
    phone: string | null;
    tags?: string[] | null;
    status?: string | null;
  } | null;
  /** Staff-only AI triage (contact_triage); one row per thread. */
  contact_triage?: TriageRow | TriageRow[] | null;
  // computed
  last_message_at?: string;
  last_message_preview?: string;
  unread_count?: number;
}

export interface MessageAttachment {
  name: string;
  path: string;
  size: number;
  type: string;
  /** When set, the file lives in this Supabase Storage bucket (e.g. call
   * recordings) and is opened via a signed URL instead of the R2 worker. */
  bucket?: string;
}

export interface AdminMessage {
  id:               string;
  thread_id:        string;
  sender_id:        string | null;
  body:             string;
  is_internal:      boolean;
  direction:        "inbound" | "outbound";
  sender_type:      "user" | "agent" | "system";
  channel:          string | null;
  source:           string | null;
  parent_message_id: string | null;
  created_at:       string;
  deleted_at:       string | null;
  attachments:      MessageAttachment[];
  // joined
  sender_profile?: {
    given_name: string | null;
    surname:    string | null;
    email:      string | null;
    profile_photo_url: string | null;
  } | null;
}

const THREAD_SELECT = `
  *,
  profiles:user_id(id, email, given_name, surname, profile_photo_url, tags, user_type, preferred_language),
  applications:application_id(id, membership_package, status),
  leads:lead_id(id, first_name, last_name, email, phone, tags, status),
  service_requests(id),
  ${THREAD_TRIAGE_EMBED}
`;

// ── All threads (admin/staff view) ───────────────────────────────
// Server-side search + pagination via get_admin_thread_ids (SECURITY INVOKER,
// so thread RLS still decides visibility); rows are then hydrated by id.
export function useAdminThreads(filters?: {
  kind?: string;
  /** "active" = open + waiting_reply */
  status?: ThreadStatus | "active" | "all";
  category?: ThreadCategory | "all";
  channel?: string;
  search?: string;
  userId?: string;
  applicationId?: string;
  limit?: number;
  /** contact_triage category key | "untriaged" | "all" */
  triage?: string;
  minPriority?: number | null;
  sort?: "recent" | "priority";
}) {
  const { user } = useAuth();
  return useQuery({
    enabled: !!user,
    queryKey: ["admin_threads", user?.id, filters],
    queryFn: async (): Promise<{ threads: AdminThread[]; total: number }> => {
      const rpcRes = await supabase.rpc("get_admin_thread_ids_v2" as never, {
        p_kind: filters?.kind ?? "all",
        p_search: filters?.search?.trim() || null,
        p_status: filters?.status && filters.status !== "all" ? filters.status : "all",
        p_channel: filters?.channel && filters.channel !== "all" ? filters.channel : "all",
        p_category: filters?.category && filters.category !== "all" ? filters.category : "all",
        p_user_id: filters?.userId ?? null,
        p_application_id: filters?.applicationId ?? null,
        p_limit: filters?.limit ?? 30,
        p_offset: 0,
        p_triage: filters?.triage ?? "all",
        p_min_priority: filters?.minPriority ?? null,
        p_sort: filters?.sort ?? "recent",
      } as never) as unknown as {
        data: { total?: number; ids?: string[] } | null;
        error: { message: string } | null;
      };
      if (rpcRes.error) throw rpcRes.error;

      const total = Number(rpcRes.data?.total ?? 0);
      const ids = rpcRes.data?.ids ?? [];
      if (ids.length === 0) return { threads: [], total };

      const { data, error } = await supabase
        .from("threads")
        .select(THREAD_SELECT)
        .in("id", ids);
      if (error) throw error;

      const byId = new Map(((data || []) as unknown as AdminThread[]).map((t) => [t.id, t]));
      const threads = ids.map((id) => byId.get(id)).filter(Boolean) as AdminThread[];
      return { threads, total };
    },
    refetchInterval: 30_000, // poll every 30s for new messages
  });
}

/** Fetch one thread with its joins — used to open deep links (?thread=) that
 * may not be on the first page of the inbox. */
export async function fetchAdminThread(threadId: string): Promise<AdminThread | null> {
  const { data } = await supabase
    .from("threads")
    .select(THREAD_SELECT)
    .eq("id", threadId)
    .maybeSingle();
  return (data as unknown as AdminThread) ?? null;
}

// ── Thread messages (admin view — sees internal notes too) ───────
export function useAdminThreadMessages(threadId: string | null | undefined) {
  const { user } = useAuth();
  return useQuery({
    queryKey: ["admin_thread_messages", threadId, user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("thread_messages")
        .select(`
          *,
          sender_profile:sender_id(given_name, surname, email, profile_photo_url)
        `)
        .eq("thread_id", threadId!)
        .order("created_at", { ascending: true });
      if (error) throw error;
      return (data || []) as AdminMessage[];
    },
    enabled: !!user && !!threadId,
    refetchInterval: 10_000, // poll every 10s while thread is open
  });
}

// ── R2 helpers — all file ops go through the CF Worker ──────────

/** Upload a message attachment to R2 via the worker /api/upload endpoint. */
export async function uploadMessageAttachment(
  file: File,
  authToken: string
): Promise<MessageAttachment> {
  const form = new FormData();
  form.append("file", file);
  form.append("type", "message");
  // no applicationId required for message type

  const res = await fetch("/api/upload", {
    method: "POST",
    headers: { Authorization: `Bearer ${authToken}` },
    body: form,
  });
  if (!res.ok) {
    const err = await res.text();
    throw new Error(`Upload failed: ${err}`);
  }
  const { key } = await res.json() as { key: string };
  return { name: file.name, path: key, size: file.size, type: file.type };
}

/** Stream/open an attachment via the worker /api/files/ endpoint. */
export function getAttachmentUrl(path: string): string {
  // Worker serves the file directly — no expiry, auth checked server-side
  return `/api/files/${path}`;
}

/** Delete an attachment from R2 via the worker DELETE /api/files/ endpoint. */
export async function deleteAttachmentFile(path: string, authToken: string): Promise<void> {
  const res = await fetch(`/api/files/${path}`, {
    method: "DELETE",
    headers: { Authorization: `Bearer ${authToken}` },
  });
  if (!res.ok) {
    console.warn("R2 delete failed:", await res.text());
  }
}

// ── Shared: get current session token ────────────────────────────
async function getToken(): Promise<string> {
  const { data } = await supabase.auth.getSession();
  return data.session?.access_token ?? "";
}

// ── Staff reply ──────────────────────────────────────────────────
export function useStaffReply() {
  const qc = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (input: {
      threadId:    string;
      body:        string;
      isInternal:  boolean;
      attachments?: MessageAttachment[];
      channel?:    string;
    }) => {
      const { data, error } = await supabase
        .from("thread_messages")
        .insert({
          thread_id:   input.threadId,
          sender_id:   user!.id,
          body:        input.body,
          is_internal: input.isInternal,
          direction:   "outbound",
          sender_type: "agent",
          channel:     (input.channel ?? null) as any,
          source:      "portal" as any,
          attachments: (input.attachments ?? []) as any,
        })
        .select()
        .single();
      if (error) throw error;

      // Update thread: status → waiting_reply, updated_at
      await supabase
        .from("threads")
        .update({
          status:     input.isInternal ? undefined : "waiting_reply",
          updated_at: new Date().toISOString(),
        })
        .eq("id", input.threadId);

      // Email + notify the member (non-blocking). The delivery badge on the
      // message refreshes when the email has been handed to the provider.
      if (!input.isInternal) {
        supabase.functions.invoke("notify-message", {
          body: {
            threadId:    input.threadId,
            messageId:   data.id,
            messageBody: input.body,
            senderId:    user!.id,
          },
        }).then(({ error: notifyError }) => {
          if (notifyError) console.error("Reply saved; member email failed", notifyError);
        }).catch(console.error).finally(() => {
          qc.invalidateQueries({ queryKey: ["thread_email_status", input.threadId] });
        });
      }

      return data;
    },
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ["admin_thread_messages", vars.threadId] });
      qc.invalidateQueries({ queryKey: ["admin_threads"] });
    },
  });
}

// ── Soft-delete a message (sets deleted_at, removes content) ────
export function useDeleteMessage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      messageId,
      threadId,
      attachments,
    }: {
      messageId:   string;
      threadId:    string;
      attachments: MessageAttachment[];
    }) => {
      // Soft delete in DB
      const { error } = await supabase
        .from("thread_messages")
        .update({
          deleted_at:  new Date().toISOString(),
          body:        "",
          attachments: [] as any,
        })
        .eq("id", messageId);
      if (error) throw error;

      // Remove R2 files via worker DELETE /api/files/
      if (attachments.length > 0) {
        const token = await getToken();
        await Promise.allSettled(
          attachments.map((a) => deleteAttachmentFile(a.path, token))
        );
      }
    },
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ["admin_thread_messages", vars.threadId] });
    },
  });
}

// ── Update thread status ─────────────────────────────────────────
export function useUpdateThreadStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ threadId, status }: { threadId: string; status: ThreadStatus }) => {
      const { error } = await supabase
        .from("threads")
        .update({ status, updated_at: new Date().toISOString() })
        .eq("id", threadId);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin_threads"] });
    },
  });
}

// ── Create thread on behalf of staff (for a user/application) ───
export function useCreateAdminThread() {
  const qc = useQueryClient();
  const { user } = useAuth();
  return useMutation({
    mutationFn: async (input: {
      subject:       string;
      body:          string;
      userId?:       string;
      applicationId?: string;
      category?:     ThreadCategory;
      isInternal?:   boolean;
    }) => {
      const { data: thread, error } = await supabase
        .from("threads")
        .insert({
          user_id:        input.userId ?? null,
          application_id: input.applicationId ?? null,
          subject:        input.subject,
          category:       (input.category ?? "general") as any,
          channel:        "portal" as any,
          status:         "open",
        })
        .select()
        .single();
      if (error) throw error;

      const { data: firstMessage } = await supabase.from("thread_messages").insert({
        thread_id:   thread.id,
        sender_id:   user!.id,
        body:        input.body,
        is_internal: input.isInternal ?? false,
        direction:   "outbound",
        sender_type: "agent",
        channel:     null,
        source:      "portal" as any,
      }).select("id").single();

      // Same as a staff reply: notify the applicant (email) unless it's an internal note.
      if (!input.isInternal) {
        supabase.functions.invoke("notify-message", {
          body: { threadId: thread.id, messageId: firstMessage?.id, messageBody: input.body, senderId: user!.id },
        }).catch(console.error).finally(() => {
          qc.invalidateQueries({ queryKey: ["thread_email_status", thread.id] });
        });
      }

      return thread;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin_threads"] }),
  });
}

// ── Mark a thread as read (upserts thread_reads row) ────────────
export async function markThreadRead(threadId: string): Promise<void> {
  try {
    await supabase.rpc("mark_thread_read", { p_thread_id: threadId });
  } catch {
    // Silently ignore if RPC not yet deployed
  }
}

// ── Unread counts per thread for the current staff member ────────
export function useUnreadCounts() {
  return useQuery({
    queryKey: ["unread_counts_by_thread"],
    queryFn: async () => {
      const { data, error } = await supabase.rpc("get_unread_counts_by_thread");
      if (error) return {} as Record<string, number>;
      const map: Record<string, number> = {};
      for (const row of (data ?? []) as { thread_id: string; unread_count: number }[]) {
        map[row.thread_id] = Number(row.unread_count);
      }
      return map;
    },
    refetchInterval: 30_000,
  });
}

// ── Total unread thread count (badge in sidebar) ─────────────────
export function useUnreadThreadCount() {
  const { data: counts = {} } = useUnreadCounts();
  return {
    data: Object.keys(counts).length,
    // expose raw map too
    counts,
  };
}
